import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Loader,
  ScrollArea,
} from "@devhop/ui";
import { Table, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { BranchTableWithStatus } from "../../domain/types";
import { useBranchTables } from "../api/queries";
import { usePOSContext } from "../context/POSContext";

type TableSelectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string | null;
};

export function TableSelectionDialog({
  open,
  onOpenChange,
  branchId,
}: TableSelectionDialogProps) {
  const { selection, setSelection } = usePOSContext();
  const { data: tables, isLoading } = useBranchTables(branchId);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);

  // Extract unique zones from tables
  const zones = useMemo(() => {
    if (!tables) return [];
    const uniqueZones = new Set<string>();
    tables.forEach((table) => {
      if (table.zone) {
        uniqueZones.add(table.zone);
      }
    });
    return Array.from(uniqueZones).sort();
  }, [tables]);

  // Filter tables by selected zone
  const filteredTables = useMemo(() => {
    if (!tables) return [];
    if (selectedZone === null) return tables;
    return tables.filter((table) => table.zone === selectedZone);
  }, [tables, selectedZone]);

  const handleTableClick = (table: BranchTableWithStatus) => {
    if (!selection) return;
    setSelection({
      ...selection,
      tableId: table.id,
      tableName: table.name,
    });
    onOpenChange(false);
  };

  const handleClear = () => {
    if (!selection) return;
    const { tableId, tableName, ...rest } = selection;
    setSelection({
      ...rest,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ເລືອກໂຕະ</DialogTitle>
        </DialogHeader>

        {!branchId || !selection ? (
          <div className="py-8">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Table className="h-8 w-8 text-muted-foreground" />
                </EmptyMedia>
                <EmptyTitle>ບໍ່ສາມາດເລືອກໂຕະໄດ້</EmptyTitle>
                <EmptyDescription>
                  ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader />
          </div>
        ) : !tables || tables.length === 0 ? (
          <div className="py-8">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Table className="h-8 w-8 text-muted-foreground" />
                </EmptyMedia>
                <EmptyTitle>ບໍ່ພົບໂຕະ</EmptyTitle>
                <EmptyDescription>ບໍ່ມີໂຕະສຳລັບສາຂານີ້</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Zone Filter */}
            {zones.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                <Button
                  variant={selectedZone === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedZone(null)}
                  className={cn(
                    "whitespace-nowrap",
                    selectedZone === null &&
                      "bg-primary text-primary-foreground",
                  )}
                >
                  ທັ້ງຫມດ
                </Button>
                {zones.map((zone) => (
                  <Button
                    key={zone}
                    variant={selectedZone === zone ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedZone(zone)}
                    className={cn(
                      "whitespace-nowrap",
                      selectedZone === zone &&
                        "bg-primary text-primary-foreground",
                    )}
                  >
                    {zone}
                  </Button>
                ))}
              </div>
            )}

            {/* Tables Grid */}
            <ScrollArea className="max-h-[400px]">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                {filteredTables.map((table) => {
                  const isSelected = selection.tableId === table.id;
                  const isOccupied = table.isOccupied;

                  return (
                    <Button
                      key={table.id}
                      variant="outline"
                      className={cn(
                        "h-20 flex-col gap-1 p-2",
                        isSelected && "ring-2 ring-primary",
                        isOccupied
                          ? "border-red-200 bg-red-50 hover:bg-red-100"
                          : "border-green-200 bg-green-50 hover:bg-green-100",
                      )}
                      onClick={() => handleTableClick(table)}
                    >
                      <span className="font-semibold text-sm">
                        {table.name}
                      </span>
                      {table.capacity && (
                        <span className="text-muted-foreground text-xs">
                          {table.capacity} ທີ່ນັ່ງ
                        </span>
                      )}
                      {isOccupied && (
                        <span className="text-destructive text-xs">ກຳລັງໃຊ້</span>
                      )}
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Selected Table Info */}
            {selection.tableId && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-muted-foreground text-sm">
                      ໂຕະທີ່ເລືອກ:
                    </span>
                    <span className="font-medium">{selection.tableName}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    className="h-8"
                  >
                    <X className="mr-2 h-4 w-4" />
                    ລຶບ
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ປິດ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
